/**
 * Caché local del último JSON devuelto por el webhook de generación de fondo (n8n).
 * Sirve cuando el móvil bloquea la pantalla, se recarga la pestaña o se pierde el contexto del fetch largo.
 *
 * No sustituye a un job en servidor + BD: para multi-dispositivo o auditoría habría que persistir en API.
 */

const DB_NAME = 'nx-panel-fondo-cache';
const STORE = 'webhookPayloadByBusiness';
const DB_VERSION = 1;

/** Tiempo máximo para ofrecer "recuperar último resultado" (7 días). */
export const FONDO_CACHE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null);
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'businessId' });
      }
    };
  });
}

function txDone(tx) {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error('abort'));
  });
}

/**
 * @param {string} businessId
 * @param {object} payload Respuesta JSON del webhook (clonable)
 */
export async function saveFondoWebhookPayload(businessId, payload) {
  if (!businessId || !payload || typeof indexedDB === 'undefined') return;
  let clone;
  try {
    clone = JSON.parse(JSON.stringify(payload));
  } catch {
    return;
  }
  try {
    const db = await openDb();
    if (!db) return;
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put({
      businessId,
      savedAt: Date.now(),
      payload: clone,
    });
    await txDone(tx);
  } catch (e) {
    console.warn('[fondoResultCache] save', e);
  }
}

/**
 * @param {string} businessId
 * @returns {Promise<{ businessId: string, savedAt: number, payload: object } | null>}
 */
export async function loadFondoWebhookPayload(businessId) {
  if (!businessId || typeof indexedDB === 'undefined') return null;
  try {
    const db = await openDb();
    if (!db) return null;
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(businessId);
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

export async function clearFondoWebhookPayload(businessId) {
  if (!businessId || typeof indexedDB === 'undefined') return;
  try {
    const db = await openDb();
    if (!db) return;
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(businessId);
    await txDone(tx);
  } catch (e) {
    console.warn('[fondoResultCache] clear', e);
  }
}

export function isFondoCacheEntryFresh(entry) {
  if (!entry?.savedAt) return false;
  return Date.now() - entry.savedAt < FONDO_CACHE_MAX_AGE_MS;
}
